// Report and timecard date ranges (legacy DateRange presets) in business dates on the location's clock. "Today" is
// the business date core assigns to now at the location (business-day start included, 04:00 by default), never the
// browser's calendar date, so a manager in another zone sees the store's day (B7/B16 FIX).
import { addDays, businessDay, isSupportedZone, type BusinessDayLocation, type LocalDate } from '@radish/core/time';

export type RangePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear';

export const PRESET_LABELS: Readonly<Record<RangePreset, string>> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 Days',
  last30: 'Last 30 Days',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  thisYear: 'This Year',
  lastYear: 'Last Year',
};

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

/** The location's business date now (falls back to the default 04:00 start; the zone must be a supported IANA zone). */
export function businessToday(zone: string, nowMs: number = Date.now()): string {
  if (!isSupportedZone(zone)) return new Date(nowMs).toISOString().slice(0, 10);
  const location: BusinessDayLocation = { zone, startTimeHistory: [] };
  return businessDay(BigInt(nowMs), location);
}

const firstOfMonth = (date: string): string => `${date.slice(0, 8)}01`;
const previousMonthStart = (date: string): string => firstOfMonth(addDays(firstOfMonth(date) as LocalDate, -1));

/**
 * The legacy presets. "Last 7 Days" runs from seven days before today to today (eight business dates, as legacy shows
 * "October 03 - October 10"); "Last 30 Days" likewise.
 */
export function presetRange(preset: RangePreset, today: string): DateRange {
  const t = today as LocalDate;
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(t, -1);
      return { from: y, to: y };
    }
    case 'last7':
      return { from: addDays(t, -7), to: today };
    case 'last30':
      return { from: addDays(t, -30), to: today };
    case 'thisMonth':
      return { from: firstOfMonth(today), to: today };
    case 'lastMonth': {
      const start = previousMonthStart(today);
      return { from: start, to: addDays(firstOfMonth(today) as LocalDate, -1) };
    }
    case 'thisYear':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case 'lastYear': {
      const year = String(Number(today.slice(0, 4)) - 1).padStart(4, '0');
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
  }
}

/** Every business date of a range, oldest first (the chart's x axis). */
export function datesIn(range: DateRange): string[] {
  const out: string[] = [];
  for (let d = range.from; d <= range.to && out.length < 4000; d = addDays(d as LocalDate, 1)) out.push(d);
  return out;
}
